/**
 * BusinessInicioHub — misma lógica visual que InicioHub (personas) / mock
 * design-mocks-business-ola-a/01-inicio.html v2:
 * greet → balances → actions → ruta → grid-3 → strip pendientes → tertiary.
 * Reusa clases inicio-hub* de index.css. Oráculo = panel overlay (no banner oro).
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import oraculoBrujula from '../assets/images/oraculo-brujula.png';

const ORACULO_SAMPLE = '¿Me alcanza para la nómina de viernes?';

function fmtCop(n) {
  const v = Number(n) || 0;
  return `$ ${Math.round(v).toLocaleString('es-CO')} COP`;
}

function fmtUsd(n) {
  const v = Number(n) || 0;
  return `$ ${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
}

function initialLetter(name) {
  const s = String(name || '').trim();
  return (s[0] || '?').toUpperCase();
}

function statusChip(status) {
  if (status === 'PENDIENTE_APROBACION') return { chip: 'Aprobar', chipBlue: false };
  if (status === 'BORRADOR') return { chip: 'Borrador', chipBlue: false };
  if (status === 'COMPLETADO') return { chip: 'Listo', chipBlue: true };
  return { chip: (status || 'Lote').replace(/_/g, ' '), chipBlue: true };
}

export default function BusinessInicioHub({
  businessName,
  nit,
  firstName,
  balanceCop = 0,
  balanceUsd = 0,
  pendientes = [],
  batches = [],
  onConvert,
  onVoice,
  onAsk,
  onNavigate,
}) {
  const navigate = useNavigate();
  const go = onNavigate || ((path) => navigate(path));

  const company = businessName || (firstName ? `Hola, ${firstName}` : 'Tu empresa');
  const sub = nit
    ? `NIT ${nit} · la cuenta de tu empresa: saldos COP/USD, pagos con cuatro ojos, tesorería y Oráculo por voz. Nadie mueve plata sin tu código.`
    : 'La cuenta de tu empresa: saldos COP/USD, pagos con cuatro ojos, tesorería y Oráculo por voz. Nadie mueve plata sin tu código.';

  const pendingPills = useMemo(() => {
    const fromPending = (pendientes || []).slice(0, 3).map((b) => {
      const { chip, chipBlue } = statusChip(b.status);
      return {
        id: b._id || `p-${b.label}`,
        nombre: b.label || 'Lote',
        meta: b.status === 'PENDIENTE_APROBACION' ? 'Espera tu OK' : 'Borrador',
        letter: initialLetter(b.label),
        chip,
        chipBlue,
        to: '/business/pagos',
      };
    });

    const pills = [...fromPending];
    if (pills.length < 4) {
      pills.push({
        id: 'cartera',
        nombre: 'Cartera',
        meta: 'Links de cobro · pendientes',
        letter: 'C',
        chip: 'Cartera',
        chipBlue: true,
        to: '/business/cartera',
      });
    }
    if (pills.length < 4) {
      pills.push({
        id: 'equipo',
        nombre: 'Equipo',
        meta: 'Operadores · dueño',
        letter: 'E',
        chip: 'Más',
        chipBlue: true,
        to: '/business/equipo',
      });
    }
    // Si no hay pendientes, teaser con lotes recientes o placeholders
    if (fromPending.length === 0 && (batches || []).length) {
      const recent = batches.slice(0, 2).map((b) => {
        const { chip, chipBlue } = statusChip(b.status);
        return {
          id: `b-${b._id}`,
          nombre: b.label || 'Lote',
          meta: b.createdAt ? new Date(b.createdAt).toLocaleDateString('es-CO') : 'Nómina',
          letter: initialLetter(b.label),
          chip,
          chipBlue,
          to: '/business/pagos',
        };
      });
      return [...recent, ...pills.filter((p) => p.id === 'cartera' || p.id === 'equipo')].slice(0, 4);
    }
    return pills.slice(0, 4);
  }, [pendientes, batches]);

  const openVoice = () => {
    if (typeof onVoice === 'function') onVoice();
  };

  const askSample = (text = ORACULO_SAMPLE) => {
    if (typeof onAsk === 'function') onAsk(text);
  };

  return (
    <div className="inicio-hub">
      <div className="inicio-hub__greet">
        <h1>{company}</h1>
        <p>{sub}</p>
      </div>

      <div className="inicio-hub__balances">
        <div className="inicio-hub__bal">
          <div className="inicio-hub__bal-lbl">COP disponible</div>
          <div className="inicio-hub__bal-val">{fmtCop(balanceCop)}</div>
          <div className="inicio-hub__bal-note">Caja empresa · Colombia</div>
        </div>
        <div className="inicio-hub__bal inicio-hub__bal--usd">
          <div className="inicio-hub__bal-lbl">USD disponible</div>
          <div className="inicio-hub__bal-val">{fmtUsd(balanceUsd)}</div>
          <div className="inicio-hub__bal-note">Saldo en dólares</div>
        </div>
      </div>

      <div className="inicio-hub__actions">
        <button type="button" className="inicio-hub__btn inicio-hub__btn--p" onClick={() => go('/business/recargar')}>
          Recargar PSE
        </button>
        <button type="button" className="inicio-hub__btn inicio-hub__btn--g" onClick={() => go('/business/cuentas-globales')}>
          Cuentas Globales
        </button>
        <button type="button" className="inicio-hub__btn inicio-hub__btn--g" onClick={() => go('/business/enviar')}>
          Enviar · proveedores
        </button>
        <button
          type="button"
          className="inicio-hub__btn inicio-hub__btn--g"
          onClick={() => (typeof onConvert === 'function' ? onConvert() : go('/business/recargar'))}
        >
          Pasar a dólares
        </button>
      </div>

      <div className="inicio-hub__ruta">
        <span className="inicio-hub__ruta-label">Ruta del dinero (empresa)</span>
        <div className="inicio-hub__ruta-steps">
          <div className="inicio-hub__ruta-step"><span className="n">1</span> Recarga COP</div>
          <span className="inicio-hub__ruta-arrow" aria-hidden="true">→</span>
          <div className="inicio-hub__ruta-step"><span className="n">2</span> Paga nómina / envía</div>
          <span className="inicio-hub__ruta-arrow" aria-hidden="true">→</span>
          <div className="inicio-hub__ruta-step"><span className="n">3</span> Tesorería / rinde</div>
        </div>
      </div>

      <div className="inicio-hub__grid3">
        <div className="inicio-hub__panel">
          <h3>Oráculo · overlay de voz</h3>
          <div className="inicio-hub__oraculo">
            <div className="inicio-hub__orb" aria-hidden="true">
              <img src={oraculoBrujula} alt="" className="inicio-hub__orb-img" />
            </div>
            <button type="button" className="inicio-hub__btn inicio-hub__btn--p" onClick={openVoice}>
              Háblale
            </button>
            <p className="inicio-hub__oraculo-copy">
              No está en el sidebar: se abre como overlay. Te cuenta caja, nómina e IVA; no ejecuta sin tu código.
            </p>
            <button type="button" className="inicio-hub__sample" onClick={() => askSample(ORACULO_SAMPLE)}>
              <span>Ejemplo</span>
              {ORACULO_SAMPLE}
            </button>
          </div>
        </div>

        <div className="inicio-hub__panel">
          <h3>Mover y cobrar</h3>
          <button type="button" className="inicio-hub__folio" onClick={() => go('/business/recargar')}>
            <div>
              <div className="name">Recargar PSE</div>
              <div className="meta">Entrada a la caja</div>
            </div>
          </button>
          <button type="button" className="inicio-hub__folio" onClick={() => go('/business/cuentas-globales')}>
            <div>
              <div className="name">Cuentas Globales</div>
              <div className="meta">ACH / SEPA / SPEI / PIX</div>
            </div>
          </button>
          <button type="button" className="inicio-hub__folio" onClick={() => go('/business/enviar')}>
            <div>
              <div className="name">Enviar</div>
              <div className="meta">Proveedores · Flowex · banco CO</div>
            </div>
          </button>
          <button type="button" className="inicio-hub__folio" onClick={() => go('/business/cartera')}>
            <div>
              <div className="name">Cartera</div>
              <div className="meta">Links de cobro · pendientes</div>
            </div>
          </button>
        </div>

        <div className="inicio-hub__panel">
          <h3>Pagar y tesorería</h3>
          <div className="inicio-hub__folio">
            <div>
              <div className="name">Pagos y nómina</div>
              <div className="meta">Planilla · cuatro ojos</div>
            </div>
            <button type="button" className="inicio-hub__btn inicio-hub__btn--g inicio-hub__btn--sm" onClick={() => go('/business/pagos')}>
              Ver
            </button>
          </div>
          <div className="inicio-hub__folio">
            <div>
              <div className="name">Blockchain</div>
              <div className="meta">Nómina en dólares on-chain</div>
            </div>
            <button type="button" className="inicio-hub__btn inicio-hub__btn--g inicio-hub__btn--sm" onClick={() => go('/business/blockchain')}>
              Ver
            </button>
          </div>
          <div className="inicio-hub__folio">
            <div>
              <div className="name">Provisiones · Caja</div>
              <div className="meta">IVA, nómina, colchón</div>
            </div>
            <button type="button" className="inicio-hub__btn inicio-hub__btn--g inicio-hub__btn--sm" onClick={() => go('/business/provisiones')}>
              Ver
            </button>
          </div>
          <div className="inicio-hub__folio">
            <div>
              <div className="name">Rendimientos</div>
              <div className="meta">USD que espera · tasa pactada</div>
            </div>
            <button type="button" className="inicio-hub__btn inicio-hub__btn--g inicio-hub__btn--sm" onClick={() => go('/business/rendimientos')}>
              Ver
            </button>
          </div>
        </div>
      </div>

      <div className="inicio-hub__agents">
        <div className="inicio-hub__agents-head">
          <h3>Pendientes · cuatro ojos</h3>
          <button type="button" className="inicio-hub__btn inicio-hub__btn--g inicio-hub__btn--sm" onClick={() => go('/business/pagos')}>
            Ver pagos
          </button>
        </div>
        <div className="inicio-hub__agents-row">
          {pendingPills.map((a) => (
            <button
              key={a.id}
              type="button"
              className="inicio-hub__agent-pill"
              onClick={() => go(a.to)}
            >
              <div className="inicio-hub__avatar">{a.letter}</div>
              <div className="inicio-hub__agent-text">
                <div className="n">{a.nombre}</div>
                <div className="m">{a.meta}</div>
              </div>
              <span className={`inicio-hub__chip${a.chipBlue ? ' inicio-hub__chip--blue' : ''}`}>{a.chip}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="inicio-hub__tertiary">
        <button type="button" className="inicio-hub__tchip" onClick={() => go('/business/calendario')}>
          Calendario fiscal
        </button>
        <button type="button" className="inicio-hub__tchip" onClick={() => go('/business/extractos')}>
          Extractos
        </button>
        <button type="button" className="inicio-hub__tchip" onClick={() => go('/business/historial')}>
          Historial
        </button>
        <button type="button" className="inicio-hub__tchip" onClick={() => go('/business/integraciones')}>
          Integraciones
        </button>
      </div>
    </div>
  );
}
